/**
 * StageNode - Individual stage representation in the pipeline timeline
 * Shows status, progress, iterations, personas, and quality metrics
 */

import React from 'react';
import './pipeline-animations.css';
import { 
  CheckCircle, 
  Play, 
  Circle, 
  AlertTriangle, 
  XCircle, 
  Clock,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { StageType, StageMetrics } from '@/types/execution';
import { cn } from '@/utils/cn';
import IterationTracker, { StageIteration } from './IterationTracker';
import PersonaBadge, { PersonaDetails, HISI_PERSONAS } from './PersonaBadge';
import QualityMetrics, { StageQualityMetrics } from './QualityMetrics';

interface StageNodeProps {
  stage: {
    id: StageType;
    label: string;
    description: string;
  };
  metrics: StageMetrics;
  isActive: boolean;
  onClick: () => void;
  layout?: 'horizontal' | 'vertical';
  className?: string;
  iterations?: StageIteration[];
  currentIteration?: number;
  maxIterations?: number;
  qualityMetrics?: StageQualityMetrics;
  activePersona?: PersonaDetails;
  showIterations?: boolean;
  showPersona?: boolean;
  showQuality?: boolean;
}

const StageNode: React.FC<StageNodeProps> = ({
  stage,
  metrics,
  isActive,
  onClick,
  layout = 'horizontal',
  className,
  iterations = [],
  currentIteration = 0,
  maxIterations = 3,
  qualityMetrics,
  activePersona,
  showIterations = true,
  showPersona = true,
  showQuality = true
}) => {
  const getStatusIcon = () => {
    switch (metrics.status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'running':
        return (
          <div className="relative">
            <Play className="w-6 h-6 text-blue-500" />
            <div className="absolute inset-0 animate-ping">
              <Play className="w-6 h-6 text-blue-400 opacity-75" />
            </div>
          </div>
        );
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'pending':
        return <Circle className="w-6 h-6 text-gray-400" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (metrics.status) {
      case 'completed':
        return 'border-green-500 bg-green-50 hover:bg-green-100';
      case 'running':
        return 'border-blue-500 bg-blue-50 hover:bg-blue-100 shadow-lg';
      case 'failed':
        return 'border-red-500 bg-red-50 hover:bg-red-100';
      case 'pending':
        return 'border-gray-300 bg-gray-50 hover:bg-gray-100';
      default:
        return 'border-yellow-500 bg-yellow-50 hover:bg-yellow-100';
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const verticalLayout = layout === 'vertical';
  
  // Get persona based on stage and current role
  const getStagePersona = (): PersonaDetails | undefined => {
    if (activePersona) return activePersona;
    
    // Map stage.id to HISI_PERSONAS keys
    const personaMap = HISI_PERSONAS[stage.id as keyof typeof HISI_PERSONAS];
    if (!personaMap) return undefined;
    
    // During running, show generator; during evaluation show evaluator
    const isEvaluating = iterations.length > 0 && 
      iterations[iterations.length - 1]?.decision !== 'accept';
    
    return isEvaluating ? personaMap.evaluator : personaMap.generator;
  };

  const currentPersona = getStagePersona();

  return (
    <div className={cn(
      'pipeline-stage-wrapper',
      verticalLayout ? 'w-full' : 'w-44',
      className
    )}>
      {/* Persona Badge - Above the main node */}
      {showPersona && currentPersona && metrics.status === 'running' && (
        <div className="mb-2">
          <PersonaBadge
            persona={currentPersona}
            isActive={true}
            compact={true}
          />
        </div>
      )}

      <button
        onClick={onClick}
        className={cn(
          'pipeline-stage-node relative group rounded-xl border-2 p-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full',
          getStatusColor(),
          isActive && 'active ring-2 ring-blue-500 ring-offset-2',
          metrics.status === 'running' && 'pipeline-stage-running',
          `pipeline-status-${metrics.status}`
        )}
      >
        {/* Progress Bar for Running Stage */}
        {metrics.status === 'running' && metrics.progress > 0 && (
          <div className="pipeline-progress-bar absolute bottom-0 left-0 h-1 bg-blue-500 rounded-b-lg"
               style={{ width: `${metrics.progress * 100}%` }} />
        )}

        <div className={cn(
          'space-y-3',
          verticalLayout ? 'flex flex-row items-start space-y-0 space-x-3' : 'flex flex-col'
        )}>
          {/* Icon and Status */}
          <div className="flex items-center space-x-2">
            <span className="pipeline-status-icon">
              {getStatusIcon()}
            </span>
            {metrics.status === 'running' && metrics.progress > 0 && (
              <span className="text-xs font-medium text-blue-600">
                {Math.round(metrics.progress * 100)}%
              </span>
            )}
          </div>

          {/* Stage Info */}
          <div className={cn(verticalLayout && 'flex-1')}>
            <h4 className="font-semibold text-gray-900 text-sm">{stage.label}</h4>
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{stage.description}</p>

            {/* Metrics */}
            <div className="mt-2 space-y-1">
              {/* Duration */}
              {metrics.duration && (
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{formatDuration(metrics.duration)}</span>
                </div>
              )}

              {/* Cost */}
              {metrics.cost_incurred > 0 && (
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <DollarSign className="w-3 h-3" />
                  <span>${(metrics.cost_incurred || 0).toFixed(2)}</span>
                </div>
              )}

              {/* Compact Quality Score (when not showing full metrics) */}
              {!showQuality && metrics.quality_score && (
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <TrendingUp className="w-3 h-3" />
                  <span>{Math.round(metrics.quality_score * 100)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </button>

      {/* Iteration Tracker - Below the main node */}
      {showIterations && (metrics.status === 'running' || iterations.length > 0) && (
        <div className="mt-2">
          <IterationTracker
            iterations={iterations}
            currentIteration={currentIteration || iterations.length}
            maxIterations={maxIterations}
            compact={true}
          />
        </div>
      )}

      {/* Quality Metrics - Below iterations */}
      {showQuality && qualityMetrics && (
        <div className="mt-2">
          <QualityMetrics
            metrics={qualityMetrics}
            compact={true}
            showDetails={false}
          />
        </div>
      )}

    </div>
  );
};

export default StageNode;